// Tenant health strip (#638): a quiet chrome badge that only exists when a terminally failed
// analysis run needs an owner's eye. It fetches its own count and renders nothing while healthy
// or on any fetch error (a broken health probe must never break the chrome).
import * as React from "react";
import { cockpit } from "./api";
import type { TenantHealthAlerts } from "./api/types";
import { useT } from "./i18n";

const REFRESH_MS = 120_000;

export function TenantHealthStrip() {
  const t = useT();
  const [alerts, setAlerts] = React.useState<TenantHealthAlerts | null>(null);

  React.useEffect(() => {
    let disposed = false;
    const load = () => {
      cockpit.getTenantHealthAlerts().then(
        (next) => {
          // A 200 body that is not the schema (proxy page, mocked {}) must render as healthy —
          // never crash the chrome. Same fail-quiet contract as a rejected fetch.
          const valid = Boolean(
            next && typeof next.retryable_failed_count === "number",
          );
          if (!disposed) setAlerts(valid ? next : null);
        },
        () => {
          if (!disposed) setAlerts(null);
        },
      );
    };
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, []);

  if (!alerts) return null;
  if (alerts.retryable_failed_count <= 0) return null;

  return (
    <div className="healthstrip" role="status">
      <span className="healthstrip__pill healthstrip__pill--critical" title={t("health.failedTitle")}>
        {t(
          alerts.retryable_failed_count === 1 ? "health.failedRuns.one" : "health.failedRuns.other",
          { count: String(alerts.retryable_failed_count) },
        )}
      </span>
    </div>
  );
}
